/**
 * Phase 9.4 — 🏆 Live Host Rankings (Public)
 * ------------------------------------------
 * Tri-column leaderboard for live hosting:
 *   1. 🎙️ Top Live Hosts (most live-gift coins received)
 *   2. 🎁 Most Gifted Live (single rooms with highest gift_total)
 *   3. 🚀 Rising Live Hosts (biggest week-over-week growth)
 *
 * Data sources:
 *   - GET /api/host/rankings/top?window=week|month|all
 *   - GET /api/host/rankings/most-gifted-live?window=week
 *   - GET /api/host/rankings/rising
 *
 * Window toggle is shared by Top + Most Gifted; Rising is always week-based
 * (the backend currently only supports a week window for that ranking).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { getProfilePhotoUrl } from "@/lib/mediaUrls";

const WINDOWS = [
  { id: "week", label: "This Week" },
  { id: "month", label: "This Month" },
  { id: "all", label: "All Time" },
];

const fmt = (n) => Number(n || 0).toLocaleString();
const growthBadge = (pct) => {
  if (pct >= 200) return { label: "🚀 Soaring", cls: "from-amber-500 to-pink-500" };
  if (pct >= 50) return { label: "🔥 Hot", cls: "from-pink-500 to-purple-500" };
  return { label: "📈 Rising", cls: "from-purple-500 to-blue-500" };
};

const HostRow = ({ rank, host, primaryLabel, primaryValue, secondaryLabel, secondaryValue, testid }) => {
  if (!host) return null;
  return (
    <Link
      to={`/artist/${host.user_id}`}
      className="flex items-center gap-3 p-3 bg-black/40 hover:bg-black/60 border border-pink-500/15 hover:border-pink-500/40 rounded-xl transition-all"
      data-testid={testid}
    >
      <div className="shrink-0 w-7 text-center">
        <span className={`text-lg font-black ${rank === 1 ? "text-amber-400" : rank === 2 ? "text-gray-300" : rank === 3 ? "text-orange-400" : "text-pink-300/70"}`}>
          {rank <= 3 ? ["🥇", "🥈", "🥉"][rank - 1] : `#${rank}`}
        </span>
      </div>
      <img
        src={getProfilePhotoUrl(host)}
        alt={host.artist_name || ""}
        className="w-10 h-10 rounded-full object-cover border-2 border-pink-500/30 shrink-0"
      />
      <div className="min-w-0 flex-1">
        <p className="text-white font-bold text-sm truncate flex items-center gap-1">
          {host.artist_name || "Anonymous"}
          {host.verified && <span className="text-blue-400 text-xs">✓</span>}
        </p>
        <p className="text-[11px] text-pink-300/70 truncate">
          {primaryLabel}: <span className="text-white font-semibold">{primaryValue}</span>
          {secondaryLabel && (
            <> · {secondaryLabel}: <span className="text-white font-semibold">{secondaryValue}</span></>
          )}
        </p>
      </div>
    </Link>
  );
};

const Section = ({ title, emoji, children, testid }) => (
  <Card
    className="bg-gradient-to-br from-pink-900/20 via-[#1a1a2e] to-purple-900/20 border border-pink-500/30 p-5"
    data-testid={testid}
  >
    <h3 className="text-base font-black text-white mb-4 flex items-center gap-2">
      <span>{emoji}</span> {title}
    </h3>
    <div className="space-y-2">{children}</div>
  </Card>
);

const LiveHostRankings = () => {
  const [windowSel, setWindowSel] = useState("week");
  const [top, setTop] = useState({ loading: true, items: [] });
  const [mostGifted, setMostGifted] = useState({ loading: true, items: [] });
  const [rising, setRising] = useState({ loading: true, items: [] });

  useEffect(() => {
    let cancelled = false;
    setTop({ loading: true, items: [] });
    setMostGifted({ loading: true, items: [] });
    (async () => {
      try {
        const [tRes, mRes] = await Promise.all([
          api.get(`/host/rankings/top?window=${windowSel}&limit=10`),
          api.get(`/host/rankings/most-gifted-live?window=${windowSel}&limit=10`),
        ]);
        if (cancelled) return;
        setTop({ loading: false, items: tRes.data.items || [] });
        setMostGifted({ loading: false, items: mRes.data.items || [] });
      } catch (_e) {
        if (!cancelled) {
          setTop({ loading: false, items: [] });
          setMostGifted({ loading: false, items: [] });
        }
      }
    })();
    return () => { cancelled = true; };
  }, [windowSel]);

  // Rising is loaded once — backend always uses week.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/host/rankings/rising?limit=10");
        if (!cancelled) setRising({ loading: false, items: r.data.items || [] });
      } catch (_e) {
        if (!cancelled) setRising({ loading: false, items: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      className="py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-900"
      data-testid="live-host-rankings-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h2 className="text-3xl md:text-4xl font-black text-white flex items-center gap-3">
              <span>🏆</span> Live Host Rankings
            </h2>
            <p className="text-sm text-pink-300/80 mt-1">
              The creators driving the live music economy on RGM.
            </p>
          </div>
          <div className="flex items-center gap-1 bg-black/40 border border-pink-500/30 rounded-full p-1" data-testid="host-rankings-window">
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                onClick={() => setWindowSel(w.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                  windowSel === w.id
                    ? "bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow"
                    : "text-pink-200/70 hover:text-white"
                }`}
                data-testid={`host-rankings-window-${w.id}`}
              >
                {w.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Top Live Hosts */}
          <Section title="Top Live Hosts" emoji="🎙️" testid="rankings-top-hosts">
            {top.loading && <p className="text-xs text-pink-300/50 text-center py-6">Loading…</p>}
            {!top.loading && top.items.length === 0 && (
              <p className="text-xs text-gray-500 text-center py-6" data-testid="rankings-top-empty">
                No live gifts in this window yet. Be the first to host!
              </p>
            )}
            {!top.loading && top.items.map((it, idx) => (
              <HostRow
                key={it.host_user_id || idx}
                rank={idx + 1}
                host={it.host || { user_id: it.host_user_id }}
                primaryLabel="🪙 Coins"
                primaryValue={fmt(it.live_gift_coins)}
                secondaryLabel="Lives"
                secondaryValue={fmt(it.lives)}
                testid={`rankings-top-row-${idx}`}
              />
            ))}
          </Section>

          {/* Most Gifted Live (rooms) */}
          <Section title="Most Gifted Live" emoji="🎁" testid="rankings-most-gifted">
            {mostGifted.loading && <p className="text-xs text-pink-300/50 text-center py-6">Loading…</p>}
            {!mostGifted.loading && mostGifted.items.length === 0 && (
              <p className="text-xs text-gray-500 text-center py-6" data-testid="rankings-most-gifted-empty">
                No gifted lives yet. Open a beam to claim the top spot.
              </p>
            )}
            {!mostGifted.loading && mostGifted.items.map((room, idx) => (
              <Link
                key={room.room_id || idx}
                to={`/beams/${room.room_id}`}
                className="flex items-center gap-3 p-3 bg-black/40 hover:bg-black/60 border border-pink-500/15 hover:border-pink-500/40 rounded-xl transition-all"
                data-testid={`rankings-most-gifted-row-${idx}`}
              >
                <div className="shrink-0 w-7 text-center">
                  <span className={`text-lg font-black ${idx === 0 ? "text-amber-400" : idx === 1 ? "text-gray-300" : idx === 2 ? "text-orange-400" : "text-pink-300/70"}`}>
                    {idx <= 2 ? ["🥇", "🥈", "🥉"][idx] : `#${idx + 1}`}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-bold text-sm truncate">
                    {room.title || "Untitled live"}
                  </p>
                  <p className="text-[11px] text-pink-300/70 truncate">
                    by <span className="text-white">{room.host?.artist_name || "—"}</span>
                    {" · "}🪙 <span className="text-white font-semibold">{fmt(room.gift_total_coins)}</span>
                    {" · "}👥 {fmt(room.participant_count)}
                  </p>
                </div>
              </Link>
            ))}
          </Section>

          {/* Rising Hosts */}
          <Section title="Rising Live Hosts" emoji="🚀" testid="rankings-rising">
            {rising.loading && <p className="text-xs text-pink-300/50 text-center py-6">Loading…</p>}
            {!rising.loading && rising.items.length === 0 && (
              <p className="text-xs text-gray-500 text-center py-6" data-testid="rankings-rising-empty">
                No rising hosts to highlight this week.
              </p>
            )}
            {!rising.loading && rising.items.map((it, idx) => {
              const badge = growthBadge(it.growth_pct || 0);
              return (
                <Link
                  key={it.host_user_id || idx}
                  to={`/artist/${it.host_user_id}`}
                  className="flex items-center gap-3 p-3 bg-black/40 hover:bg-black/60 border border-pink-500/15 hover:border-pink-500/40 rounded-xl transition-all"
                  data-testid={`rankings-rising-row-${idx}`}
                >
                  <img
                    src={getProfilePhotoUrl(it.host || { user_id: it.host_user_id })}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover border-2 border-pink-500/30 shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-white font-bold text-sm truncate">
                      {it.host?.artist_name || "Anonymous"}
                    </p>
                    <p className="text-[11px] text-pink-300/70 truncate">
                      +{fmt(it.growth_pct)}% this week · 🪙 {fmt(it.this_week_coins)}
                    </p>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r ${badge.cls} text-white shrink-0`}>
                    {badge.label}
                  </span>
                </Link>
              );
            })}
          </Section>
        </div>

        <div className="mt-6 flex justify-center">
          <Link to="/beams">
            <Button
              className="bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white font-bold px-6"
              data-testid="live-host-rankings-cta"
            >
              🎙️ Browse Live Beams →
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default LiveHostRankings;
